import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDirty } from '../hooks/useDirty'
import { usePageNavigationGuard } from '../hooks/useUnsavedChangesGuard'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch, ApiError } from '../lib/api'
import { useSectionAccess, type AgentTabKey } from '../lib/roleAccess'
import { FEATURES } from '../lib/features'
import { AGENT_CATEGORIES } from '../lib/types'
import { toAgentSlug, useResolveAgentId } from '../lib/agentSlug'
import type {
  AgentDetailResponse,
  AgentUpdate,
  AgentCategory,
  SkillRef,
  SkillsRef,
  ConnectionsRef,
  ConnectionBindingRef,
  ConnectionOperationRef,
  KnowledgeSourceRef,
  KnowledgeSourcesRef,
  GuardrailConfig,
  GuardrailFormState,
  AccessRef,
  AccessPermission,
  ModelConfiguration,
  ModelConfigurationListResponse,
  Skill,
  SkillListResponse,
  Connection,
  ConnectionListResponse,
} from '../lib/types'
import { Spinner } from '../components/Spinner'
import { InfoIcon } from '../components/icons/InfoIcon'
import { PageSaveDiscardActions } from '../components/PageSaveDiscardActions'
import { Card } from '../components/Card'
import { InfoTooltip } from '../components/InfoTooltip'
import { PageHeader } from '../components/PageHeader'
import { MdEditor } from '../components/MdEditor'
import { SkillsTab } from './SkillsTab'
import { ConnectionsTab } from './ConnectionsTab'
import { KnowledgeTab } from './KnowledgeTab'
import { GuardrailsTab } from './GuardrailsTab'
import { AccessTab } from './AccessTab'

const MAX_DNA_UPLOAD_BYTES = 1024 * 1024

/* ─── DNA section editor ─── */

interface DnaEditorProps {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  readOnly?: boolean
  sparkName?: string
}

function DnaEditor({ id, label, value, onChange, placeholder, readOnly = false, sparkName }: DnaEditorProps) {
  const { t } = useTranslation()
  const filenameBase = sparkName ? `${toAgentSlug(sparkName)}-${id.replace('dna-', '')}` : id

  return (
    <div>
      <label htmlFor={id} className="sr-only">{label}</label>
      <MdEditor
        id={id}
        value={value}
        onChange={onChange}
        filename={`${filenameBase}.md`}
        placeholder={placeholder}
        readOnly={readOnly}
        previewFirst
        maxUploadBytes={MAX_DNA_UPLOAD_BYTES}
        invalidTypeMessage={t('agentDetail.identity.uploadErrorType')}
        tooLargeMessage={t('agentDetail.identity.uploadErrorSize')}
        uploadTitle={t('agentDetail.identity.replaceMarkdown')}
        previewTitle={t('common.preview')}
        editTitle={t('agentDetail.identity.editSource')}
        readOnlyTitle={t('agentDetail.identity.readOnlyTitle', { label })}
      />
    </div>
  )
}

/* ─── Shared input/textarea styles ─── */

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--text-primary)',
  background: 'var(--bg-input)',
  outline: 'none',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  fontSize: 13,
  marginBottom: 6,
  color: 'var(--text-primary)',
}

/* ─── Avatar (header) ───
   Every Agent shows the same fixed picture — the Mambu mark on a filled square.
   There is no per-Agent picker (no emoji, no image upload). */

function AgentAvatar() {
  return (
    <div className="agent-avatar mambu-mark-icon" aria-hidden="true">
      <img src="/mambu-logo.png" alt="" />
    </div>
  )
}

/* ─── Tab definitions ─── */

type TabId = 'dna' | 'skills' | 'connections' | 'knowledge' | 'guardrails' | 'access'

interface TabDef {
  id: TabId
  label: string
  preview?: boolean
}

// Labels are resolved inside the component via useTranslation
const TAB_IDS: { id: TabId; preview?: boolean }[] = [
  { id: 'dna' },
  { id: 'skills' },
  { id: 'connections' },
  { id: 'knowledge' },
  { id: 'guardrails' },
  { id: 'access' },
]

/* URL aliases (R-NAV-8): the `:tab` route segment differs from the internal
 * TabId for three tabs (`agent-dna`, `knowledge-base`, `agent-access`),
 * matching the prototype's `renderBreadcrumb` alias table. */
const TAB_ID_TO_URL: Record<TabId, string> = {
  dna: 'agent-dna',
  skills: 'skills',
  connections: 'connections',
  knowledge: 'knowledge-base',
  guardrails: 'guardrails',
  access: 'agent-access',
}
const TAB_URL_TO_ID: Record<string, TabId> = Object.fromEntries(
  Object.entries(TAB_ID_TO_URL).map(([id, url]) => [url, id as TabId])
)

/* One PUT endpoint segment per tab (MS-741 §3.1) — replaces the umbrella
 * `PUT /api/v1/agents/{id}` save. Distinct from `TAB_ID_TO_URL` (the route's
 * `:tab` alias): `agent-dna` happens to match here, but is a coincidence, not
 * a guarantee — the two tables serve different contracts. */
const TAB_SAVE_URL: Record<TabId, string> = {
  dna: 'agent-dna',
  skills: 'skills',
  connections: 'connections',
  knowledge: 'knowledge-base',
  guardrails: 'guardrails',
  access: 'agent-access',
}

/* Audit-log `changedSection` label per tab — not user-facing UI copy, so no
 * t() lookup is needed here. */
const CHANGED_SECTION_LABEL: Record<TabId, string> = {
  dna: 'Agent DNA',
  skills: 'Skills',
  connections: 'Connections',
  knowledge: 'Knowledge Base',
  guardrails: 'Guardrails',
  access: 'Access',
}

/* ─── Identity form state ─── */

interface IdentityFormState {
  displayName: string
  description: string
  category: AgentCategory
  avatarType: 'emoji' | 'image'
  avatarImageUrl: string | null
}

/* ─── Identity section (read/edit) ─── */

interface IdentitySectionProps {
  form: IdentityFormState
  onChange: (patch: Partial<IdentityFormState>) => void
  isMambuBuilt: boolean
}

function IdentitySection({ form, onChange, isMambuBuilt }: IdentitySectionProps) {
  const editing = !isMambuBuilt
  const { t } = useTranslation()

  return (
    <Card title={t('agentDetail.identity.title')} subtitle={t('agentDetail.identity.subtitle')}>
      {editing || isMambuBuilt ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label htmlFor="identity-display-name" style={labelStyle}>{t('agentDetail.identity.nameLabel')}</label>
              <input
                id="identity-display-name"
                type="text"
                value={form.displayName}
                onChange={(e) => onChange({ displayName: e.target.value })}
                required
                disabled={isMambuBuilt}
                style={{ ...inputStyle, opacity: isMambuBuilt ? 0.55 : 1 }}
              />
            </div>
            <div>
              <label htmlFor="identity-category" style={labelStyle}>{t('agentDetail.identity.categoryLabel')}</label>
              <select
                id="identity-category"
                value={form.category}
                onChange={(e) => onChange({ category: e.target.value as AgentCategory })}
                disabled={isMambuBuilt}
                style={{ ...inputStyle, opacity: isMambuBuilt ? 0.55 : 1 }}
              >
                {AGENT_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <label htmlFor="identity-description" style={labelStyle}>{t('agentDetail.identity.descriptionLabel')}</label>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{form.description.length}/150</span>
            </div>
            <textarea
              id="identity-description"
              value={form.description}
              onChange={(e) => onChange({ description: e.target.value.slice(0, 150) })}
              maxLength={150}
              rows={3}
              placeholder={t('agentDetail.identity.descriptionPlaceholder')}
              disabled={isMambuBuilt}
              style={{ ...inputStyle, resize: 'vertical', opacity: isMambuBuilt ? 0.55 : 1 }}
            />
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <div style={labelStyle}>{t('agentDetail.identity.nameLabel')}</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{form.displayName || <em style={{ color: 'var(--text-tertiary)' }}>{t('common.notSet')}</em>}</div>
            </div>
            <div>
              <div style={labelStyle}>{t('agentDetail.identity.categoryLabel')}</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{form.category}</div>
            </div>
          </div>
          <div>
            <div style={labelStyle}>{t('agentDetail.identity.descriptionLabel')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
              {form.description || <em style={{ color: 'var(--text-tertiary)' }}>{t('common.noDescription')}</em>}
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

/* ─── DNA form state ─── */

interface DnaFormState {
  purpose: string
  behavior: string
  domainContext: string
}

/* ─── Spark DNA tab (merged Identity + DNA) ─── */

interface SparkDnaTabProps {
  identityForm: IdentityFormState
  onIdentityChange: (patch: Partial<IdentityFormState>) => void
  dnaForm: DnaFormState
  onDnaChange: (patch: Partial<DnaFormState>) => void
  modelId: string
  onModelIdChange: (modelId: string) => void
  models: ModelConfiguration[]
  isMambuBuilt: boolean
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void
}

function SparkDnaTab({ identityForm, onIdentityChange, dnaForm, onDnaChange, modelId, onModelIdChange, models, isMambuBuilt, navigate }: SparkDnaTabProps) {
  const { t } = useTranslation()
  return (
    <div>
      <IdentitySection form={identityForm} onChange={onIdentityChange} isMambuBuilt={isMambuBuilt} />

      <Card title={t('agentDetail.purpose.title')} subtitle={t('agentDetail.purpose.subtitle')}>
        <DnaEditor
          id="dna-purpose"
          label={t('agentDetail.purpose.title')}
          value={dnaForm.purpose}
          onChange={(v) => onDnaChange({ purpose: v })}
          placeholder="Describe the purpose of this Agent…"
          readOnly={isMambuBuilt}
          sparkName={identityForm.displayName}
        />
      </Card>

      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t('agentDetail.behaviour.title')}
            {isMambuBuilt && (
              <InfoTooltip icon={<InfoIcon size={13} />} width={280}>
                {t('agentDetail.behaviour.mambuBuiltTooltip')}
              </InfoTooltip>
            )}
          </span>
        }
        subtitle={isMambuBuilt ? t('agentDetail.behaviour.mambuBuiltSubtitle') : t('agentDetail.behaviour.subtitle')}
        defaultCollapsed={isMambuBuilt}
        locked={isMambuBuilt}
      >
        {!isMambuBuilt && (
          <DnaEditor
            id="dna-behavior"
            label={t('agentDetail.behaviour.title')}
            value={dnaForm.behavior}
            onChange={(v) => onDnaChange({ behavior: v })}
            placeholder="Describe how this Agent should behave…"
            sparkName={identityForm.displayName}
          />
        )}
      </Card>

      <Card title={t('agentDetail.domainContext.title')} subtitle={t('agentDetail.domainContext.subtitle')}>
        <DnaEditor
          id="dna-domain-context"
          label={t('agentDetail.domainContext.title')}
          value={dnaForm.domainContext}
          onChange={(v) => onDnaChange({ domainContext: v })}
          placeholder="Describe the tenant-specific context injected at runtime…"
          sparkName={identityForm.displayName}
        />
      </Card>

      {FEATURES.agentModelSelector && (
        <Card
          title={t('agentDetail.model.title')}
          subtitle={t('agentDetail.model.subtitle')}
          actions={<button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => navigate('/global-settings/models')}>{t('common.manageInGlobalSettings')}</button>}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={modelId}
              onChange={(e) => onModelIdChange(e.target.value)}
              style={{ ...inputStyle, marginBottom: 0 }}
            >
              {models.map((model) => (
                <option key={model.id} value={model.id}>{model.modelName}</option>
              ))}
            </select>
          </div>
        </Card>
      )}
    </div>
  )
}


/* ─── Access form defaults ─── */

const DEFAULT_ACCESS_FORM: AccessRef = {
  permissions: [],
  defaultAccessConfig: { grantsInteractionToAllTenantUsers: false },
}

/* ─── Guardrail form defaults ─── */

const DEFAULT_GUARDRAIL_FORM: GuardrailFormState = {
  requireHumanConfirmation: true,
  escalateOnUncertainty: true,
  monetaryApprovalThreshold: '',
  monetaryApprovalCurrency: 'EUR',
  allowBulkOperations: false,
  allowOutOfHoursActions: false,
  dryRunMode: false,
  sessionTimeoutMinutes: 30,
  restrictToAgentScope: true,
  showConfidenceScores: true,
  showDataSourceCitations: true,
  allowInternetAccess: true,
  responseLanguage: 'und',
}

function guardrailConfigToForm(config: GuardrailConfig): GuardrailFormState {
  return {
    ...config,
    monetaryApprovalThreshold: config.monetaryApprovalThreshold ?? '',
    monetaryApprovalCurrency: config.monetaryApprovalCurrency ?? 'EUR',
  }
}

function guardrailFormToConfig(form: GuardrailFormState): GuardrailConfig {
  const hasThreshold = form.monetaryApprovalThreshold.trim() !== ''
  return {
    ...form,
    monetaryApprovalThreshold: hasThreshold ? form.monetaryApprovalThreshold : null,
    monetaryApprovalCurrency: hasThreshold ? form.monetaryApprovalCurrency || null : null,
  }
}

function getBoundConnectionOps(
  c: ConnectionBindingRef,
  globalConn: Connection | undefined
): ConnectionOperationRef[] {
  return c.operations
    .filter(o => {
      if (!o.isEnabled) return true
      const globalOp = globalConn?.operations.find(op => op.id === o.operationId)
      return globalOp ? globalOp.isEnabled && !globalOp.isDeprecated : true
    })
    .map(o => {
      const globalOp = globalConn?.operations.find(op => op.id === o.operationId)
      return globalOp?.isDeprecated ? { ...o, isEnabled: false } : o
    })
}

function getUnboundConnectionOps(
  c: ConnectionBindingRef,
  globalConn: Connection | undefined
): ConnectionOperationRef[] {
  return (globalConn?.operations ?? [])
    .filter(op => !op.isDeprecated && op.id != null && !c.operations.some(o => o.operationId === op.id))
    .map(op => ({ operationId: op.id!, isEnabled: false }))
}

function buildConnectionUpdate(
  c: ConnectionBindingRef,
  globalConn: Connection | undefined
): ConnectionBindingRef {
  const boundOps = getBoundConnectionOps(c, globalConn)
  const unboundOps = getUnboundConnectionOps(c, globalConn)
  return { ...c, operations: [...boundOps, ...unboundOps] }
}

/* ─── Main SparkDetail export ─── */

export function AgentDetail() {
  const { id: idOrSlug, tab: tabParam } = useParams<{ id: string; tab: string }>()
  const { agentId: id, isResolving } = useResolveAgentId(idOrSlug ?? '')
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  /* ── Form state ── */
  const [identityForm, setIdentityForm] = useState<IdentityFormState>({
    displayName: '',
    description: '',
    category: 'Other',
    avatarType: 'emoji',
    avatarImageUrl: null,
  })
  const [dnaForm, setDnaForm] = useState<DnaFormState>({
    purpose: '',
    behavior: '',
    domainContext: '',
  })
  const [modelId, setModelId] = useState('')
  const [savedModelId, setSavedModelId] = useState('')
  const [skillsForm, setSkillsForm] = useState<SkillsRef>({ local: [], global: [], mambu: [] })
  const [connectionsForm, setConnectionsForm] = useState<ConnectionsRef>([])
  const [knowledgeSourcesForm, setKnowledgeSourcesForm] = useState<KnowledgeSourcesRef>({ local: [], global: [] })
  const [guardrailsForm, setGuardrailsForm] = useState<GuardrailFormState>(DEFAULT_GUARDRAIL_FORM)
  const [accessForm, setAccessForm] = useState<AccessRef>(DEFAULT_ACCESS_FORM)

  /* ── Saved state (for dirty comparison) ── */
  const [savedIdentity, setSavedIdentity] = useState<IdentityFormState>(identityForm)
  const [savedDna, setSavedDna] = useState<DnaFormState>(dnaForm)
  const [savedSkills, setSavedSkills] = useState<SkillsRef>({ local: [], global: [], mambu: [] })
  const [skillsInitialized, setSkillsInitialized] = useState(false)
  const [savedConnections, setSavedConnections] = useState<ConnectionsRef>([])
  const [connectionsInitialized, setConnectionsInitialized] = useState(false)
  const [savedKnowledgeSources, setSavedKnowledgeSources] = useState<KnowledgeSourcesRef>({ local: [], global: [] })
  const [knowledgeSourcesInitialized, setKnowledgeSourcesInitialized] = useState(false)
  const [savedGuardrails, setSavedGuardrails] = useState<GuardrailFormState>(DEFAULT_GUARDRAIL_FORM)
  const [guardrailsInitialized, setGuardrailsInitialized] = useState(false)
  const [savedAccess, setSavedAccess] = useState<AccessRef>(DEFAULT_ACCESS_FORM)
  const [accessInitialized, setAccessInitialized] = useState(false)

  /* ── Spark metadata (read-only in form) ── */
  const [sparkVersion, setSparkVersion] = useState(0)
  const [isMambuBuilt, setIsMambuBuilt] = useState(false)
  const [isReadOnly, setIsReadOnly] = useState(false)

  /* ── UI state ──
     activeTab is derived from the URL's :tab segment, not local state — the
     tab is addressable (T-URL-05). An unrecognized segment redirects to the
     first tab rather than silently falling back, so a typo'd URL still lands
     somewhere addressable instead of a page frozen on "agent-dna" for a
     different URL. */
  const activeTab: TabId = TAB_URL_TO_ID[tabParam ?? ''] ?? 'dna'
  const { canOpenAgentTab, isRoleResolved } = useSectionAccess()
  useEffect(() => {
    if (tabParam && !TAB_URL_TO_ID[tabParam]) {
      navigate(`/agents/${idOrSlug}/${TAB_ID_TO_URL.dna}`, { replace: true })
      return
    }
    // Never decide a role-based redirect on an unresolved role — see
    // SectionAccess.isRoleResolved.
    if (!isRoleResolved) return
    // Deep link to a role-hidden agent tab (MS-741 AC3.8) — routes to Home, not to
    // the DNA tab, since the whole Agents surface may be role-hidden for this
    // caller too.
    const tabId = TAB_URL_TO_ID[tabParam ?? '']
    if (tabId && !canOpenAgentTab(tabId as AgentTabKey)) {
      navigate('/home', { replace: true })
    }
  }, [tabParam, idOrSlug, navigate, canOpenAgentTab, isRoleResolved])
  const [pendingTab, setPendingTab] = useState<TabId | null>(null)
  const [tabSwitchDialog, setTabSwitchDialog] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [conflictError, setConflictError] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [skillsDraftDirty, setSkillsDraftDirty] = useState(false)
  const [knowledgeDraftDirty, setKnowledgeDraftDirty] = useState(false)

  /* ── Per-tab dirty tracking ──
     `base` reflects only each tab's own controlled-form state (what page-level Save can
     persist). `combined` also folds in local entity-editor drafts (Skills slide-over,
     Knowledge Base add/rename panels) that have no page-level Save path of their own —
     see the tab-switch dialog below, which uses the split to avoid offering "Save" for
     something Save can't actually persist. */
  const tabDirtyMap = useMemo<{
    base: Partial<Record<TabId, boolean>>
    combined: Partial<Record<TabId, boolean>>
  }>(() => {
    const isDnaDirty = (): boolean => {
      if (
        identityForm.displayName !== savedIdentity.displayName ||
        identityForm.description !== savedIdentity.description ||
        identityForm.category !== savedIdentity.category ||
        identityForm.avatarType !== savedIdentity.avatarType ||
        identityForm.avatarImageUrl !== savedIdentity.avatarImageUrl ||
        dnaForm.purpose !== savedDna.purpose ||
        dnaForm.behavior !== savedDna.behavior ||
        dnaForm.domainContext !== savedDna.domainContext ||
        modelId !== savedModelId
      ) return true
      return false
    }

    const isSkillsDirty = (): boolean => {
      const sortRef = (a: SkillRef, b: SkillRef) => a.skillId.localeCompare(b.skillId)
      const localForm = [...skillsForm.local].sort(sortRef)
      const localSaved = [...savedSkills.local].sort(sortRef)
      const globalForm = [...skillsForm.global].sort(sortRef)
      const globalSaved = [...savedSkills.global].sort(sortRef)
      const mambuForm = [...skillsForm.mambu].sort(sortRef)
      const mambuSaved = [...savedSkills.mambu].sort(sortRef)
      if (
        localForm.length !== localSaved.length ||
        globalForm.length !== globalSaved.length ||
        mambuForm.length !== mambuSaved.length
      ) return true
      for (let i = 0; i < localForm.length; i++) {
        if (localForm[i].skillId !== localSaved[i].skillId || localForm[i].isEnabled !== localSaved[i].isEnabled) return true
      }
      for (let i = 0; i < globalForm.length; i++) {
        if (globalForm[i].skillId !== globalSaved[i].skillId || globalForm[i].isEnabled !== globalSaved[i].isEnabled) return true
      }
      for (let i = 0; i < mambuForm.length; i++) {
        if (mambuForm[i].skillId !== mambuSaved[i].skillId || mambuForm[i].isEnabled !== mambuSaved[i].isEnabled) return true
      }
      return false
    }

    const isConnectionsDirty = (): boolean => {
      const sortConn = (a: ConnectionBindingRef, b: ConnectionBindingRef) => a.connectionId.localeCompare(b.connectionId)
      const sortOp = (a: ConnectionOperationRef, b: ConnectionOperationRef) => a.operationId.localeCompare(b.operationId)
      const connForm = [...connectionsForm].sort(sortConn).map(c => ({ ...c, operations: [...c.operations].sort(sortOp) }))
      const connSaved = [...savedConnections].sort(sortConn).map(c => ({ ...c, operations: [...c.operations].sort(sortOp) }))
      return JSON.stringify(connForm) !== JSON.stringify(connSaved)
    }

    const isKnowledgeDirty = (): boolean => {
      const sortKb = (a: KnowledgeSourceRef, b: KnowledgeSourceRef) => a.knowledgeSourceId.localeCompare(b.knowledgeSourceId)
      const kbLocalForm = [...knowledgeSourcesForm.local].sort(sortKb)
      const kbLocalSaved = [...savedKnowledgeSources.local].sort(sortKb)
      const kbGlobalForm = [...knowledgeSourcesForm.global].sort(sortKb)
      const kbGlobalSaved = [...savedKnowledgeSources.global].sort(sortKb)
      if (JSON.stringify(kbLocalForm) !== JSON.stringify(kbLocalSaved)) return true
      if (JSON.stringify(kbGlobalForm) !== JSON.stringify(kbGlobalSaved)) return true
      return false
    }

    const isGuardrailsDirty = (): boolean => {
      return JSON.stringify(guardrailsForm) !== JSON.stringify(savedGuardrails)
    }

    const isAccessDirty = (): boolean => {
      const sortPerm = (a: AccessPermission, b: AccessPermission) => a.principalId.localeCompare(b.principalId)
      const accessFormSorted = { ...accessForm, permissions: [...accessForm.permissions].sort(sortPerm) }
      const savedAccessSorted = { ...savedAccess, permissions: [...savedAccess.permissions].sort(sortPerm) }
      return JSON.stringify(accessFormSorted) !== JSON.stringify(savedAccessSorted)
    }

    const base: Partial<Record<TabId, boolean>> = {
      dna: isDnaDirty(),
      skills: isSkillsDirty(),
      connections: isConnectionsDirty(),
      knowledge: isKnowledgeDirty(),
      guardrails: isGuardrailsDirty(),
      access: isAccessDirty(),
    }
    return {
      base,
      combined: {
        ...base,
        skills: base.skills || skillsDraftDirty,
        knowledge: base.knowledge || knowledgeDraftDirty,
      },
    }
  }, [identityForm, dnaForm, savedIdentity, savedDna, modelId, savedModelId, skillsForm, savedSkills, connectionsForm, savedConnections, knowledgeSourcesForm, savedKnowledgeSources, guardrailsForm, savedGuardrails, accessForm, savedAccess, skillsDraftDirty, knowledgeDraftDirty])

  const isDirty = tabDirtyMap.combined[activeTab] ?? false
  const anyDirty = Object.values(tabDirtyMap.combined).some(Boolean)

  const { setDirty: publishDirty, clearDirty } = useDirty('agent-detail')
  useEffect(() => { publishDirty(anyDirty) }, [anyDirty, publishDirty])

  const TAB_LABEL_MAP: Record<TabId, string> = useMemo(() => ({
    dna: t('tabs.agentDna'),
    skills: t('tabs.skills'),
    connections: t('tabs.connections'),
    knowledge: t('tabs.knowledgeBase'),
    guardrails: t('tabs.guardrails'),
    access: t('tabs.access'),
  }), [t])

  const TABS: TabDef[] = useMemo(() =>
    TAB_IDS
      .filter(tab => canOpenAgentTab(tab.id))
      .map(tab => ({ ...tab, label: TAB_LABEL_MAP[tab.id] })),
    [TAB_LABEL_MAP, canOpenAgentTab]
  )

  /* ── Fetch spark detail ── */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agent', id],
    queryFn: () => apiFetch<AgentDetailResponse>(`/api/v1/agents/${id}`),
    enabled: !!id && !isResolving,
  })

  /* ── Fetch available models ── */
  const { data: modelsData } = useQuery({
    queryKey: ['models'],
    queryFn: () => apiFetch<ModelConfigurationListResponse>('/api/v1/models'),
  })

  /* ── Fetch global skills for filtering globally-disabled resources ── */
  const { data: globalSkillsData } = useQuery({
    queryKey: ['skills', 'global'],
    queryFn: () => apiFetch<SkillListResponse>('/api/v1/skills?scope=Global'),
  })

  /* ── Fetch global connections for filtering globally-disabled resources ── */
  const { data: globalConnectionsData } = useQuery({
    queryKey: ['connections'],
    queryFn: () => apiFetch<ConnectionListResponse>('/api/v1/connections'),
  })

  const globalSkills: Skill[] = globalSkillsData?.items ?? []
  const globalConnections: Connection[] = globalConnectionsData?.items ?? []

  const isConnectionsInvalid = globalConnections.some(conn =>
    conn.isEssential && conn.isEnabled &&
    !(connectionsForm.find(b => b.connectionId === conn.id)?.isEnabled ?? false)
  )

  /* ── Sync fetched data to form (only on first load or explicit re-sync) ── */
  useEffect(() => {
    if (!data || initialized) return
    const identity: IdentityFormState = {
      displayName: data.agent.displayName,
      description: data.agent.description ?? '',
      category: data.agent.category,
      avatarType: data.agent.avatarType,
      avatarImageUrl: data.agent.avatarImageUrl,
    }
    const dna: DnaFormState = {
      purpose: data.dna.purpose ?? '',
      behavior: data.dna.behavior ?? '',
      domainContext: data.dna.domainContext ?? '',
    }
    const skills: SkillsRef = {
      local: data.skills?.local ?? [],
      global: data.skills?.global ?? [],
      mambu: data.skills?.mambu ?? [],
    }
    const connections: ConnectionsRef = data.connections ?? []
    // Local KB bindings are always enabled — there is no per-spark toggle for them.
    // Force isEnabled: true on load to repair any stale false values in the DB.
    const rawKb = data.knowledgeSources ?? { local: [], global: [] }
    const knowledgeSources: KnowledgeSourcesRef = {
      ...rawKb,
      local: rawKb.local.map(b => ({ ...b, isEnabled: true })),
    }
    const guardrails: GuardrailFormState = data.guardrails
      ? guardrailConfigToForm(data.guardrails)
      : DEFAULT_GUARDRAIL_FORM
    setIdentityForm(identity)
    setSavedIdentity(identity)
    setDnaForm(dna)
    setSavedDna(dna)
    setModelId(data.agent.modelConfigurationId)
    setSavedModelId(data.agent.modelConfigurationId)
    setSkillsForm(skills)
    setSavedSkills(skills)
    setSkillsInitialized(true)
    setConnectionsForm(connections)
    setSavedConnections(connections)
    setConnectionsInitialized(true)
    setKnowledgeSourcesForm(knowledgeSources)
    setSavedKnowledgeSources(knowledgeSources)
    setKnowledgeSourcesInitialized(true)
    setGuardrailsForm(guardrails)
    setSavedGuardrails(guardrails)
    setGuardrailsInitialized(true)
    const access: AccessRef = data.access ?? DEFAULT_ACCESS_FORM
    setAccessForm(access)
    setSavedAccess(access)
    setAccessInitialized(true)
    setSparkVersion(data.agent.version)
    setIsMambuBuilt(data.agent.isMambuBuilt)
    setIsReadOnly(data.agent.isReadOnly)
    setInitialized(true)
  }, [data, initialized])

  /* ── In-app navigation guard (left-nav, breadcrumb, brand click — all route
     through react-router's navigate()) — consolidated hook checks page-level
     anyDirty AND the entity-editor registry. ── */
  const { dialogOpen: navBlockDialog, confirmLeave: confirmNavLeave, cancelLeave: cancelNavLeave } = usePageNavigationGuard(anyDirty, clearDirty)

  /* ── Save mutation ───
     One PUT to the active tab's own endpoint (MS-741 §3.1), not the whole
     agent to the umbrella endpoint — `tabDirtyMap` already tells us which tab
     is dirty, and the page refuses a tab switch while dirty, so a Save never
     carries more than one tab's changes. */
  const saveMutation = useMutation({
    mutationFn: ({ update, tab }: { update: AgentUpdate; tab: TabId }) =>
      apiFetch<AgentDetailResponse>(`/api/v1/agents/${id}/${TAB_SAVE_URL[tab]}`, {
        method: 'PUT',
        body: JSON.stringify(update),
      }),
    onSuccess: (updated, { tab }) => {
      // Always update version (spark-wide)
      setSparkVersion(updated.agent.version)

      // Only sync saved state for the active tab
      if (tab === 'dna') {
        const identity: IdentityFormState = {
          displayName: updated.agent.displayName,
          description: updated.agent.description ?? '',
          category: updated.agent.category,
          avatarType: updated.agent.avatarType,
          avatarImageUrl: updated.agent.avatarImageUrl,
        }
        const dna: DnaFormState = {
          purpose: updated.dna.purpose ?? '',
          behavior: updated.dna.behavior ?? '',
          domainContext: updated.dna.domainContext ?? '',
        }
        setIdentityForm(identity)
        setSavedIdentity(identity)
        setDnaForm(dna)
        setSavedDna(dna)
        setSavedModelId(updated.agent.modelConfigurationId)
        setModelId(updated.agent.modelConfigurationId)
      } else if (tab === 'skills' && updated.skills) {
        const skills: SkillsRef = {
          local: updated.skills.local ?? [],
          global: updated.skills.global ?? [],
          mambu: updated.skills.mambu ?? [],
        }
        setSavedSkills(skills)
        setSkillsForm(skills)
      } else if (tab === 'connections' && updated.connections) {
        setSavedConnections(updated.connections)
        setConnectionsForm(updated.connections)
      } else if (tab === 'knowledge' && updated.knowledgeSources) {
        setSavedKnowledgeSources(updated.knowledgeSources)
        setKnowledgeSourcesForm(updated.knowledgeSources)
      } else if (tab === 'guardrails' && updated.guardrails) {
        const g = guardrailConfigToForm(updated.guardrails)
        setSavedGuardrails(g)
        setGuardrailsForm(g)
      } else if (tab === 'access' && updated.access) {
        setSavedAccess(updated.access)
        setAccessForm(updated.access)
      }

      setSaveError(null)
      setConflictError(false)

      void queryClient.invalidateQueries({ queryKey: ['agents'] })
      // Also drop this agent's own detail entry — the form is patched in place
      // here, but the cached GET still holds the pre-save snapshot, so leaving
      // and re-entering the agent within the app-wide `staleTime` window would
      // re-render removed rows / reverted fields.
      queryClient.invalidateQueries({ queryKey: ['agent', id] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setConflictError(true)
      } else if (err instanceof ApiError && err.code === 'access.lastAdmin') {
        setSaveError(t('agentDetail.lastAdminError'))
      } else {
        setSaveError(err instanceof Error ? err.message : t('agentDetail.saveFailedFallback'))
      }
    },
  })

  /* ── Handlers ── */

  function handleDiscard() {
    // Only discard changes for the active tab
    if (activeTab === 'dna') {
      setIdentityForm(savedIdentity)
      setDnaForm(savedDna)
      setModelId(savedModelId)
    } else if (activeTab === 'skills') {
      setSkillsForm(savedSkills)
    } else if (activeTab === 'connections') {
      setConnectionsForm(savedConnections)
    } else if (activeTab === 'knowledge') {
      setKnowledgeSourcesForm(savedKnowledgeSources)
    } else if (activeTab === 'guardrails') {
      setGuardrailsForm(savedGuardrails)
    } else if (activeTab === 'access') {
      setAccessForm(savedAccess)
    }
    setSaveError(null)
    setConflictError(false)
  }

  // Only include identity fields in the save payload if something changed.
  function getIdentityUpdate(): AgentUpdate['identity'] | undefined {
    const identityChanged = identityForm.displayName !== savedIdentity.displayName ||
                           identityForm.category !== savedIdentity.category ||
                           (identityForm.description || null) !== (savedIdentity.description || null) ||
                           identityForm.avatarType !== savedIdentity.avatarType ||
                           identityForm.avatarImageUrl !== savedIdentity.avatarImageUrl
    if (!identityChanged) return undefined
    return {
      displayName: identityForm.displayName,
      description: identityForm.description || null,
      category: identityForm.category,
      avatarType: 'image',
      avatarImageUrl: identityForm.avatarImageUrl ?? null,
    }
  }

  // Only include DNA fields in the save payload if something changed.
  function getDnaUpdate(): AgentUpdate['dna'] | undefined {
    const dnaChanged = (dnaForm.purpose || null) !== (savedDna.purpose || null) ||
                      (dnaForm.behavior || null) !== (savedDna.behavior || null) ||
                      (dnaForm.domainContext || null) !== (savedDna.domainContext || null)
    if (!dnaChanged) return undefined
    return {
      purpose: dnaForm.purpose || null,
      // behavior is redacted (null) from the API for Mambu-built agents; never
      // echo it back, or the read-only guard sees it as "no change" and clears it.
      ...(isMambuBuilt ? {} : { behavior: dnaForm.behavior || null }),
      domainContext: dnaForm.domainContext || null,
    }
  }

  // For global bindings: filter out any that are marked enabled but whose underlying
  // global skill has been disabled by an admin (backend also validates, this is defensive).
  // Local skills are spark-owned — no global-disable concept applies, send as-is.
  function getSkillsUpdate(): AgentUpdate['skills'] {
    return {
      local: skillsForm.local,
      global: skillsForm.global.filter(s => !s.isEnabled || globalSkills.some(skill => skill.id === s.skillId && skill.isEnabled)),
      mambu: skillsForm.mambu,
    }
  }

  // Filter out globally-disabled connections and operations that are marked as enabled.
  // Also include unbound non-deprecated global ops (isEnabled: false) so binding rows are
  // created and the "New" badge clears after save.
  function getConnectionsUpdate(): AgentUpdate['connections'] {
    return connectionsForm
      .filter(c => !c.isEnabled || globalConnections.some(conn => conn.id === c.connectionId && conn.isEnabled))
      .map(c => buildConnectionUpdate(c, globalConnections.find(conn => conn.id === c.connectionId)))
  }

  // Applies the DNA tab's changed fields (identity, DNA, model) onto the save payload.
  function applyDnaUpdate(update: AgentUpdate) {
    const identity = getIdentityUpdate()
    if (identity) update.identity = identity
    const dna = getDnaUpdate()
    if (dna) update.dna = dna
    if (modelId !== savedModelId) {
      update.modelConfigurationId = modelId
    }
  }

  function handleSave() {
    setSaveError(null)
    setConflictError(false)
    const update: AgentUpdate = {
      version: sparkVersion,
      changedSection: CHANGED_SECTION_LABEL[activeTab],
    }

    // Only include fields for the active tab
    if (activeTab === 'dna') {
      applyDnaUpdate(update)
    } else if (activeTab === 'skills' && skillsInitialized) {
      update.skills = getSkillsUpdate()
    } else if (activeTab === 'connections' && connectionsInitialized) {
      update.connections = getConnectionsUpdate()
    } else if (activeTab === 'knowledge' && knowledgeSourcesInitialized) {
      update.knowledgeSources = {
        ...knowledgeSourcesForm,
        local: knowledgeSourcesForm.local.map(b => ({ ...b, isEnabled: true })),
      }
    } else if (activeTab === 'guardrails' && guardrailsInitialized) {
      update.guardrails = guardrailFormToConfig(guardrailsForm)
    } else if (activeTab === 'access' && accessInitialized) {
      update.access = accessForm
    }

    saveMutation.mutate({ update, tab: activeTab })
  }

  /* ── Skill binding callbacks ── */

  function handleLocalToggle(skillId: string, isEnabled: boolean) {
    setSkillsForm(prev => ({
      ...prev,
      local: prev.local.map(s => s.skillId === skillId ? { ...s, isEnabled } : s),
    }))
  }

  function handleGlobalToggle(skillId: string, isEnabled: boolean) {
    setSkillsForm(prev => {
      const exists = prev.global.find(s => s.skillId === skillId)
      if (exists) {
        return { ...prev, global: prev.global.map(s => s.skillId === skillId ? { ...s, isEnabled } : s) }
      }
      return { ...prev, global: [...prev.global, { skillId, isEnabled }] }
    })
  }

  function handleMambuToggle(skillId: string, isEnabled: boolean) {
    setSkillsForm(prev => ({
      ...prev,
      mambu: prev.mambu.map(s => s.skillId === skillId ? { ...s, isEnabled } : s),
    }))
  }

  /* Entity CRUD (local skills / knowledge sources) mutates bindings server-side
     through its own endpoint, so the cached agent detail GET is stale afterwards
     even though the form is patched in place here. */
  function invalidateAgentDetail() {
    queryClient.invalidateQueries({ queryKey: ['agent', id] })
  }

  function handleLocalSkillAdded(binding: SkillRef) {
    setSkillsForm(prev => ({ ...prev, local: [...prev.local, binding] }))
    setSavedSkills(prev => ({ ...prev, local: [...prev.local, binding] }))
    invalidateAgentDetail()
  }

  function handleLocalSkillRemoved(skillId: string) {
    setSkillsForm(prev => ({ ...prev, local: prev.local.filter(s => s.skillId !== skillId) }))
    setSavedSkills(prev => ({ ...prev, local: prev.local.filter(s => s.skillId !== skillId) }))
    invalidateAgentDetail()
  }

  /* ── Connection binding callbacks ── */

  function handleConnectionToggle(connectionId: string, isEnabled: boolean) {
    setConnectionsForm(prev => {
      const exists = prev.find(c => c.connectionId === connectionId)
      if (exists) {
        return prev.map(c => c.connectionId === connectionId ? { ...c, isEnabled } : c)
      }
      return [...prev, { connectionId, isEnabled, operations: [] }]
    })
  }

  function handleOperationToggle(connectionId: string, operationId: string, isEnabled: boolean) {
    setConnectionsForm(prev => {
      const connIdx = prev.findIndex(c => c.connectionId === connectionId)
      if (connIdx === -1) {
        return [...prev, { connectionId, isEnabled: true, operations: [{ operationId, isEnabled }] }]
      }
      const conn = prev[connIdx]
      const opExists = conn.operations.find(o => o.operationId === operationId)
      const updatedOps = opExists
        ? conn.operations.map(o => o.operationId === operationId ? { ...o, isEnabled } : o)
        : [...conn.operations, { operationId, isEnabled }]
      const updated = { ...conn, operations: updatedOps }
      return prev.map((c, i) => i === connIdx ? updated : c)
    })
  }

  /* ── Knowledge source binding callbacks ── */

  function handleKbGlobalToggle(sourceId: string, isEnabled: boolean) {
    setKnowledgeSourcesForm(prev => {
      const exists = prev.global.find(b => b.knowledgeSourceId === sourceId)
      if (exists) {
        return { ...prev, global: prev.global.map(b => b.knowledgeSourceId === sourceId ? { ...b, isEnabled } : b) }
      }
      return { ...prev, global: [...prev.global, { knowledgeSourceId: sourceId, isEnabled }] }
    })
  }

  function handleKbSourceAdded(binding: KnowledgeSourceRef) {
    setKnowledgeSourcesForm(prev => ({ ...prev, local: [...prev.local, binding] }))
    setSavedKnowledgeSources(prev => ({ ...prev, local: [...prev.local, binding] }))
    invalidateAgentDetail()
  }

  function handleKbSourceRemoved(sourceId: string) {
    setKnowledgeSourcesForm(prev => ({ ...prev, local: prev.local.filter(b => b.knowledgeSourceId !== sourceId) }))
    setSavedKnowledgeSources(prev => ({ ...prev, local: prev.local.filter(b => b.knowledgeSourceId !== sourceId) }))
    invalidateAgentDetail()
  }

  /** Commits a tab switch by navigating to its URL — the single writer of
   * the active tab (see activeTab derivation above). */
  function commitTab(tabId: TabId) {
    navigate(`/agents/${idOrSlug}/${TAB_ID_TO_URL[tabId]}`)
  }

  function handleRefetch() {
    setConflictError(false)
    setSaveError(null)
    setInitialized(false)
    void refetch()
  }

  /* ── Render ── */

  if (isLoading || isResolving) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={32} />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 32px' }}>
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '16px 20px', color: 'var(--danger)', fontSize: 13 }}>
          {error instanceof Error ? error.message : t('agentDetail.failedToLoad')}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* ── .sparks-main wrapper — header + tabs + scrollable content ── */}
      <div className="sparks-main">
        {/* ── Header ── */}
        <PageHeader
          variant="detail"
          icon={<AgentAvatar />}
          title={
            <>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {identityForm.displayName || t('agentDetail.unnamedAgent')}
              </span>
              {isMambuBuilt && (
                <span className="badge badge-mambu">
                  {t('common.mambuBuilt')}
                </span>
              )}
              {FEATURES.agentVersionBadge && (
                <span className="badge">
                  {t('agentDetail.versionBadge', { version: sparkVersion })}
                </span>
              )}
            </>
          }
          caption={identityForm.description || undefined}
          actions={
            <PageSaveDiscardActions
              dirty={isDirty}
              saving={saveMutation.isPending}
              onSave={handleSave}
              onDiscard={handleDiscard}
              saveLabel={t('agentDetail.saveChanges')}
              discardLabel={t('agentDetail.discardChanges')}
            />
          }
        />

        {/* ── Tabs ── */}
        <div
          role="tablist"
          tabIndex={-1}
          aria-label={t('agentDetail.tabsAriaLabel')}
          className="tabs"
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            e.preventDefault()
            const navigableTabs = TABS.filter(t => !t.preview)
            const currentIndex = navigableTabs.findIndex(t => t.id === activeTab)
            if (currentIndex === -1) return
            const delta = e.key === 'ArrowRight' ? 1 : -1
            const nextIndex = (currentIndex + delta + navigableTabs.length) % navigableTabs.length
            const nextTab = navigableTabs[nextIndex]
            document.getElementById(`tab-${nextTab.id}`)?.focus()
            if (isDirty) {
              setPendingTab(nextTab.id)
              setTabSwitchDialog(true)
            } else {
              commitTab(nextTab.id)
            }
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id
            const showDnaError = tab.id === 'dna' && !isMambuBuilt && identityForm.displayName.trim() === ''
            return (
              <button
                key={tab.id}
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.id}`}
                type="button"
                tabIndex={isActive || tab.preview ? 0 : -1}
                onClick={() => {
                  if (tab.preview) return
                  if (isDirty && !isActive) {
                    setPendingTab(tab.id)
                    setTabSwitchDialog(true)
                  } else {
                    commitTab(tab.id)
                  }
                }}
                disabled={tab.preview}
                className={`tab-btn${isActive ? ' active' : ''}${tab.preview ? ' preview' : ''}`}
                style={tab.preview ? { color: 'var(--text-tertiary)', cursor: 'default' } : undefined}
              >
                {tab.label}
                {tab.id === 'connections' && isConnectionsInvalid && (
                  <span aria-label="error" style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                    background: 'var(--danger)', marginLeft: 4, flexShrink: 0, verticalAlign: 'middle',
                  }} />
                )}
                {tab.preview && (
                  <>
                    {' '}
                    <span className="nav-badge-soon" title={t('common.previewTooltip')}>
                      {t('common.preview')}
                    </span>
                  </>
                )}
                {showDnaError && (
                  <span
                    aria-label={t('agentDetail.dnaTabError')}
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'var(--danger)',
                      marginLeft: 4,
                      verticalAlign: 'middle',
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* ── Scrollable content area ── */}
        <div className="tab-scroll-area">

        {/* ── Save error banner ── */}
        {saveError && (
          <div
            role="alert"
            style={{
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
              borderRadius: 8,
              padding: '10px 16px',
              color: 'var(--danger)',
              fontSize: 13,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>{saveError}</span>
            <button
              type="button"
              onClick={() => setSaveError(null)}
              aria-label={t('agentDetail.dismissError')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        )}

        {/* ── Conflict (409) error banner ── */}
        {conflictError && (
          <div
            role="alert"
            style={{
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning)',
              borderRadius: 8,
              padding: '10px 16px',
              color: 'var(--warning)',
              fontSize: 13,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>{t('agentDetail.conflictError')}</span>
            <button
              type="button"
              onClick={handleRefetch}
              style={{
                padding: '4px 12px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                background: 'var(--warning)',
                color: 'var(--text-inverse)',
                border: 'none',
                flexShrink: 0,
              }}
            >
              {t('common.reFetch')}
            </button>
          </div>
        )}

        {/* ── Tab panels ── */}
        <div
          className="tab-panel active"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'dna' && (
            <SparkDnaTab
              identityForm={identityForm}
              onIdentityChange={(patch) => setIdentityForm(prev => ({ ...prev, ...patch }))}
              dnaForm={dnaForm}
              onDnaChange={(patch) => setDnaForm(prev => ({ ...prev, ...patch }))}
              modelId={modelId}
              onModelIdChange={setModelId}
              models={modelsData?.items ?? []}
              isMambuBuilt={isMambuBuilt}
              navigate={navigate}
            />
          )}
          {activeTab === 'skills' && id && (
            <SkillsTab
              agentId={id}
              isReadOnly={isReadOnly}
              localBindings={skillsForm.local}
              globalBindings={skillsForm.global}
              mambuBindings={skillsForm.mambu}
              onLocalToggle={handleLocalToggle}
              onGlobalToggle={handleGlobalToggle}
              onMambuToggle={handleMambuToggle}
              onLocalSkillAdded={handleLocalSkillAdded}
              onLocalSkillRemoved={handleLocalSkillRemoved}
              onDraftDirtyChange={setSkillsDraftDirty}
            />
          )}
          {activeTab === 'connections' && id && (
            <ConnectionsTab
              agentId={id}
              connectionBindings={connectionsForm}
              onConnectionToggle={handleConnectionToggle}
              onOperationToggle={handleOperationToggle}
              isReadOnly={isReadOnly}
            />
          )}
          {activeTab === 'knowledge' && id && (
            <KnowledgeTab
              agentId={id}
              localBindings={knowledgeSourcesForm.local}
              globalBindings={knowledgeSourcesForm.global}
              onGlobalToggle={handleKbGlobalToggle}
              onSourceAdded={handleKbSourceAdded}
              onSourceRemoved={handleKbSourceRemoved}
              onDraftDirtyChange={setKnowledgeDraftDirty}
            />
          )}
          {activeTab === 'guardrails' && (
            <GuardrailsTab
              form={guardrailsForm}
              onChange={(patch) => setGuardrailsForm(prev => ({ ...prev, ...patch }))}
            />
          )}
          {activeTab === 'access' && (
            <AccessTab
              form={accessForm}
              onChange={(patch) => setAccessForm(prev => ({ ...prev, ...patch }))}
              agentName={identityForm.displayName || t('agentDetail.unnamedAgent')}
            />
          )}

        </div>
        </div>
      </div>
      {/* ── End .sparks-main ── */}

      {/* ── Unsaved-changes navigation guard ── */}
      {navBlockDialog && (
        <UnsavedChangesDialog onConfirm={confirmNavLeave} onCancel={cancelNavLeave} />
      )}

      {/* ── Tab-switch guard dialog ── */}
      {tabSwitchDialog && pendingTab && (
        <UnsavedChangesDialog
          onConfirm={() => {
            handleDiscard()
            setTabSwitchDialog(false)
            commitTab(pendingTab)
            setPendingTab(null)
          }}
          onCancel={() => {
            setTabSwitchDialog(false)
            setPendingTab(null)
          }}
        />
      )}
    </div>
  )
}
